import React, { useCallback } from 'react';
import type {
  GoodsDiscountGroup,
  GoodsDiscountGroupItem,
} from 'shared/src/api';
import { Checkbox, Flex, IconDownload, Tag, Typography } from '@banhala/ui-kit';
import { bridge, isNil, isNotNil, localizeNumber } from 'shared/src/utils';
import { usePostRedeemCoupon } from '../../../queries/usePostRedeemCoupon';
import { useLogClickCheckBox } from '../hooks/useLogClickCheckBox';
import { useLogCompleteDownloadCoupon } from '../hooks/useLogCompleteDownloadCoupon';
import { useIsAnonymousUser, useSafeTimeout } from 'shared/src/hooks';

export const DiscountItemView: React.FC<{
  group: GoodsDiscountGroup;
  item: GoodsDiscountGroupItem;
  checked: GoodsDiscountGroupItem[];
  onChangeChecked: (checked: GoodsDiscountGroupItem[]) => void;
}> = ({ group, checked, item, onChangeChecked }) => {
  const isChecked =
    checked.findIndex(checkItem => `${checkItem.key}` === `${item.key}`) !== -1;
  // const { data: myInfo } = useMyInfo();
  const isAnonymouseUser = useIsAnonymousUser();
  const { mutateAsync: postRedeemCoupon } = usePostRedeemCoupon();

  const isStatic = group.display_type === 'STATIC';
  const logClickCheckBox = useLogClickCheckBox(item);
  const logCompleteDownloadCoupon = useLogCompleteDownloadCoupon(item);
  const setSafeTimeout = useSafeTimeout();

  const onPressItem = useCallback(() => {
    if (isStatic) return;

    if (isChecked) {
      logClickCheckBox(false);
      onChangeChecked(checked.filter(check => check.key !== item.key));
    } else {
      logClickCheckBox(true);
      onChangeChecked(checked.concat([item]));
    }
  }, [isStatic, isChecked, logClickCheckBox, onChangeChecked, checked, item]);

  const onPressIssuableCoupon = useCallback(() => {
    if (isNil(item.detail)) return;

    if (isAnonymouseUser) {
      bridge.assertLogin({});
      setSafeTimeout(() => {
        bridge.toast('로그인이나 회원가입이 필요해요');
      }, 200);
      return;
    }

    postRedeemCoupon({ sno: item.detail.sno })
      .then(logCompleteDownloadCoupon)
      .then(() => {
        bridge.toast('쿠폰이 발급되었습니다');
      })
      .catch(ex => {
        const error = ex as Error;
        bridge.toast(error.message ?? '쿠폰을 발급받지 못했습니다');
      });
  }, [
    item.detail,
    isAnonymouseUser,
    postRedeemCoupon,
    logCompleteDownloadCoupon,
    setSafeTimeout,
  ]);

  return (
    <Flex>
      <Flex.Row
        alignItems={'center'}
        padding={{
          vertical: isStatic ? 8 : 4,
        }}
      >
        <Flex.Row
          flex={1}
          alignItems={'center'}
          gap={8}
          onPress={onPressItem}
          padding={{ right: 8 }}
        >
          {!isStatic && (
            <Checkbox value={isChecked} onPress={onPressItem}></Checkbox>
          )}
          <Flex.Row flex={1}>
            <Typography type={'body3'} numberOfLines={1}>
              {item.title}
            </Typography>
          </Flex.Row>

          {isNotNil(item.detail) && group.display_type === 'ISSUABLE' && (
            <Flex onPress={onPressIssuableCoupon}>
              {item.detail.is_issued ? (
                <Tag size={'small'} type={'secondary'}>
                  보유중
                </Tag>
              ) : (
                <Tag
                  size={'small'}
                  color={'blue'}
                  type={'secondary'}
                  icon={IconDownload}
                >
                  받기
                </Tag>
              )}
            </Flex>
          )}
        </Flex.Row>
        <Typography
          type={'subtitle3'}
          color={isChecked ? 'content_primary' : 'content_tertiary'}
        >
          -{localizeNumber(item.amount)}원
        </Typography>
      </Flex.Row>
    </Flex>
  );
};
